import { ComponentType } from '@angular/cdk/portal';
import { AfterViewInit, Component, HostListener, OnDestroy, OnInit, ViewChild, inject, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose } from '@angular/material/dialog';
import { RootCauseSelectComponent } from '../../components/field-components/root-cause-select/root-cause-select.component';
import { DiagnosisEventNote } from '../../models/diagnosis-event-note';
import { FilterSelectItem } from '../../models/filter-select-item';
import { DiagnosisEventItem } from '../../models/diagnosis-event-item';
import { DiagnosisEventService } from '../../services/diagnosis-event/diagnosis-event.service';
import { DiagnosisEventEditRequest } from '../../models/diagnosis-event-edit-request';
import { DiagnosisEventQuery } from '../../models/diagnosis-event-query';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { DecimalPipe, DatePipe } from '@angular/common';
import { finalize, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { OverlayLoadingDirective } from '../../directives/overlay-loading.directive';
import { ColDef } from 'ag-grid-community';
import { AssociatedDxEvent } from '../../models/associated-dx-event';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatTooltip } from '@angular/material/tooltip';

type EditOpptyDialogMode = 'single' | 'batch';

export class EditOpptyDialogData {
  items?: DiagnosisEventItem[];
  statusStateIDs?: number[];
  parent?: string;
  mode: EditOpptyDialogMode = 'single';
  allEnabled?: boolean;
  allSamePreferredActionFromServer?: boolean;
  queryResultCount?: number;
  query?: DiagnosisEventQuery;
  exportEnabled = false;
  targeted?: boolean;
}

export class EditOpptyDialogModel {
  readonly component: ComponentType<any> = EditOpptyDialogComponent;
  panelClass?: string | string[] = ['dialog-container', 'small-grid-dialog-container'];
  autoFocus?: boolean = false;
  data?: EditOpptyDialogData;
  disableClose? = true;

  constructor(data: EditOpptyDialogData) {
    this.data = data;
  }
}

@Component({
    selector: 'app-edit-oppty-dialog',
    templateUrl: './edit-oppty-dialog.component.html',
    styleUrls: ['./edit-oppty-dialog.component.scss'],
    imports: [
        MatDialogTitle,
        CdkScrollable,
        MatDialogContent,
        OverlayLoadingDirective,
        MatRadioGroup,
        FormsModule,
        ReactiveFormsModule,
        MatRadioButton,
        RootCauseSelectComponent,
        MatFormField,
        MatInput,
        MatLabel,
        MatDialogActions,
        MatButton,
        MatDialogClose,
        DecimalPipe,
        DatePipe,
        AgGridModule,
        MatCheckbox,
        MatTooltip,
    ]
})
export class EditOpptyDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  public data = inject(MAT_DIALOG_DATA) as EditOpptyDialogData;
  editDialogForm: FormGroup;
  rootCauseControlName = 'edit-oppty-root-cause';
  rcReasonControlName = 'edit-oppty-rc-reason';
  notesControlName = 'edit-oppty-notes';
  batchTypeControlName = 'assign-mode';
  closeNonTargetedControlName = 'close-non-targeted';

  batchTypeControl = new FormControl('default', [Validators.required]);
  hideGrids = signal<boolean>(false);

  rcReasonControl = new FormControl('', [Validators.required]);
  notesControl = new FormControl('');
  closeNonTargetedControl = new FormControl(true);

  singleItem?: DiagnosisEventItem;
  notesHistory: DiagnosisEventNote[] = [];

  formReady = false;
  showRcReason = false;
  actionTaken = signal<boolean>(true);
  rootCauseDisabled = signal<boolean>(false);

  readonly nonActionableUserDefinedStatusId = 1;
  readonly completeStatusId = 2;
  readonly researchEmrPreferredAction = 'research emr';

  batchStatusStats = '';
  batchPreferredActionStats = '';
  selectedBatchCount = 0;

  loadingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  loading: Observable<boolean>;
  associatedNonTargetedRows: AssociatedDxEvent[] = [];
  associatedNonTargetedCount: number = 0;
  private targetedGridReady = false;
  private nonTargetedDataLoaded = false;
  private nonTargetedGridReady = false;
  private rootCauseOptionsReady = false;
  private destroy$ = new Subject<void>();

  get canEditAllResults(): boolean {
    return !!this.data.allEnabled && !!this.data.allSamePreferredActionFromServer;
  }

  get showTargetedSection(): boolean {
    return !!this.data.targeted;
  }

  get closeNonTargetedValue(): boolean {
    return this.showTargetedSection && !!this.closeNonTargetedControl.value;
  }

  get isAllResultsSelection(): boolean {
    return this.canEditAllResults && this.batchTypeControl?.value == 'all';
  }

  @ViewChild('editRootCauseSelect') rootCauseComp?: RootCauseSelectComponent;
  @ViewChild('targetedGrid') targetedGrid?: AgGridAngular;
  @ViewChild('nonTargetedGrid') nonTargetedGrid?: AgGridAngular;

  defaultColDef: ColDef = {
    editable: false,
    sortable: false,
    filter: false,
    suppressMovable: true,
    suppressHeaderMenuButton: true,
  };

  associatedNonTargetedDefaultColDef: ColDef = {
    editable: false,
    sortable: false,
    filter: false,
    suppressMovable: true,
    suppressHeaderMenuButton: true,
  };

  columnDefs: ColDef[] = [
    { field: 'Payer', headerName: 'Payer', headerTooltip: 'Payer', valueFormatter: this.payerValue, tooltip: this.payerTooltip, minWidth: 68, maxWidth: 68 },
    { field: 'DateOfService', headerName: 'DOS', type: 'numericColumn', headerTooltip: 'DOS', minWidth: 82, maxWidth: 82 },
    { field: 'VisitID', headerName: 'Visit ID', type: 'numericColumn', headerTooltip: 'Visit ID', minWidth: 72, maxWidth: 72 },
    { field: 'ICDCODE', headerName: 'ICD10', type: 'numericColumn', headerTooltip: 'ICD10', minWidth: 72, maxWidth: 72 },
    { field: 'EMRPID', headerName: 'EMR PID', type: 'numericColumn', headerTooltip: 'EMR Patient ID', minWidth: 78, maxWidth: 78 },
    { field: 'MBI', headerName: 'MBI', type: 'numericColumn', headerTooltip: 'MBI', flex: 1 },
    { field: 'PatientFirstName', headerName: 'Patient First Name', headerTooltip: 'Patient First Name', flex: 1 },
    { field: 'PatientLastName', headerName: 'Patient Last Name', headerTooltip: 'Patient Last Name', flex: 1 },
    { field: 'PreferredAction', headerName: 'Preferred Action', headerTooltip: 'Preferred Action', flex: 1, valueFormatter: this.filterOptionValue },
  ];

  associatedNonTargetedColumnDefs: ColDef[] = [
    { field: 'Payer', headerName: 'Payer', headerTooltip: 'Payer', valueFormatter: this.payerValue, tooltip: this.payerTooltip, minWidth: 68, maxWidth: 68 },
    { field: 'DateOfService', headerName: 'DOS', type: 'numericColumn', headerTooltip: 'DOS', minWidth: 82, maxWidth: 82 },
    { field: 'VisitID', headerName: 'Visit ID', type: 'numericColumn', headerTooltip: 'Visit ID', minWidth: 72, maxWidth: 72 },
    { field: 'ICDCode', headerName: 'ICD10', type: 'numericColumn', headerTooltip: 'ICD10', minWidth: 72, maxWidth: 72 },
    { field: 'EMRPID', headerName: 'EMR PID', type: 'numericColumn', headerTooltip: 'EMR Patient ID', minWidth: 78, maxWidth: 78 },
    { field: 'MBI', headerName: 'MBI', type: 'numericColumn', headerTooltip: 'MBI', flex: 1 },
    { field: 'PatientFirstName', headerName: 'Patient First Name', headerTooltip: 'Patient First Name', flex: 1 },
    { field: 'PatientLastName', headerName: 'Patient Last Name', headerTooltip: 'Patient Last Name', flex: 1 },
    { field: 'PreferredAction', headerName: 'Preferred Action', headerTooltip: 'Preferred Action', flex: 1, valueFormatter: this.filterOptionValue },
  ];

  closeNonTargetedTooltip = 'Update all open diagnosis events that are associated to the Targeted Event(s) '
    + 'to the status of Complete (Closed) with a root cause of NonTargeted Event.';

  constructor
  (
    public dialogRef: MatDialogRef<EditOpptyDialogComponent>,
    private opptyService: DiagnosisEventService,
    private fb: FormBuilder
  ) 
  { 
    this.editDialogForm = this.fb.group({});
    this.loading = this.loadingSubject.asObservable();
  }

  @HostListener('window:resize')
  onResize() {
    this.targetedGrid?.api?.sizeColumnsToFit();
    this.nonTargetedGrid?.api?.sizeColumnsToFit();
  }

  ngOnInit(): void {
    this.loadingSubject.next(true);
    this.selectedBatchCount = this.data.items?.length || 0;

    this.nonTargetedDataLoaded = !this.showTargetedSection;
    this.nonTargetedGridReady = !this.showTargetedSection;

    this.editDialogForm.addControl(this.notesControlName, this.notesControl);

    if(this.data.mode == 'single') {
      this.singleItem = this.data?.items ? this.data.items[0] : undefined;
      if (!this.singleItem?.ID) {
        return; // should not happen, but just in case, to avoid unnecessary service call with invalid ID
      }
      this.opptyService.getNotesByEventID(this.singleItem.ID).subscribe(result => {
        this.notesHistory = result;
      });
    }

    if(this.data.mode == 'batch') {
      this.getBatchStats();
    }

    if(this.data.mode == 'batch' && this.canEditAllResults) {
      this.editDialogForm.addControl(this.batchTypeControlName, this.batchTypeControl);
      this.batchTypeControl.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(value => this.onBatchTypeChanged(value));
    }

    if (this.showTargetedSection) {
      this.editDialogForm.addControl(this.closeNonTargetedControlName, this.closeNonTargetedControl);

      // get associated events
      const request: DiagnosisEventEditRequest = new DiagnosisEventEditRequest();
      if (this.data.mode == 'batch') {
        request.EventIDs = this.data.items ? this.data.items.map(i => i.ID).join(',') : undefined;
      } else if (this.data.mode == 'single') {
        request.EventIDs = this.singleItem?.ID?.toString();
      }
      request.Query = { IsOpptyQuery: this.data.parent == 'oppty-list' } as DiagnosisEventQuery;

      this.opptyService.getAssociatedEvents(request)
        .pipe(finalize(() => {
          this.nonTargetedDataLoaded = true;
          this.tryHideInitialLoading();
        }))
        .subscribe({
          next: result => {
            if (result?.events) {
              this.associatedNonTargetedRows = result.events;
              this.associatedNonTargetedCount = result.count ?? 0;
            }
            else {
              this.resetAssociatedNonTargetedState();
            }
          },
          error: () => {
            this.resetAssociatedNonTargetedState();
          }
        });
    }
  }

  ngAfterViewInit(): void {
    this.rootCauseComp?.getOptionData([]);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onBatchTypeChanged(value: string | null) {
    const pageCount = this.data.items?.length || 0;
    const queryCount = this.data.queryResultCount || pageCount;

    this.selectedBatchCount = value == 'all' ? queryCount : pageCount;

    if (!this.showTargetedSection) {
      return;
    }

    this.loadingSubject.next(true);
    this.nonTargetedDataLoaded = false;
    const request: DiagnosisEventEditRequest = new DiagnosisEventEditRequest();

    if (value == 'all') {
      this.hideGrids.set(true);
      request.Query = this.data.query;
    } else {
      this.hideGrids.set(false);
      request.EventIDs = this.data.items ? this.data.items.map(i => i.ID).join(',') : undefined;
      request.Query = { IsOpptyQuery: this.data.parent == 'oppty-list' } as DiagnosisEventQuery;
    }

    this.opptyService.getAssociatedEvents(request)
      .pipe(finalize(() => {
        this.nonTargetedDataLoaded = true;
        this.tryHideInitialLoading();
      }))
      .subscribe({
        next: result => {
          if (result?.events) {
            this.associatedNonTargetedCount = result.count ?? 0;
            return;
          }

          this.associatedNonTargetedCount = 0;
        },
        error: () => {
          this.associatedNonTargetedCount = 0;
        }
      });
  }

  onRootCauseReady() {
    this.updateRootCauseMode();
    this.formReady = true;
    this.rootCauseOptionsReady = true;
    this.tryHideInitialLoading();
  }

  onActionTakenChanged(value: boolean) {
    this.actionTaken.set(value);
    this.updateRootCauseMode();
  }

  updateRootCauseMode() {
    if(!this.rootCauseComp) {
      return;
    }

    if(!this.actionTaken()) {
      this.rootCauseDisabled.set(false);
      this.applyRootCauseFilter(this.nonActionableUserDefinedStatusId);
      return;
    }

    if(this.isResearchEmrPreferredAction()) {
      this.rootCauseDisabled.set(false);
      this.applyRootCauseFilter(this.completeStatusId);
      return;
    }

    this.rootCauseDisabled.set(true);
    this.rootCauseComp.filteredOptions = [];
    this.clearRootCauseSelection();
  }

  applyRootCauseFilter(statusId: number) {
    if(!this.rootCauseComp) {
      return;
    }

    const currentValue = this.rootCauseComp.control.value as FilterSelectItem | null;

    this.rootCauseComp.filterOptionsFromStatusIds([statusId]);

    const isCurrentValueAvailable = currentValue
      ? this.rootCauseComp.filteredOptions.some(o => o.ID == currentValue.ID)
      : true;

    if(isCurrentValueAvailable) {
      this.onRootCauseChanged(currentValue);
      return;
    }

    if(this.data.mode == 'single' && this.singleItem?.RootCause?.ID) {
      const isSingleValueAvailable = this.rootCauseComp.filteredOptions.some(o => o.ID == this.singleItem?.RootCause?.ID);
      if(isSingleValueAvailable) {
        this.rootCauseComp.setSingleModeValue(this.singleItem.RootCause);
        this.onRootCauseChanged(this.rootCauseComp.control.value);
        return;
      }
    }

    if(currentValue) {
      this.clearRootCauseSelection();
      return;
    }

    this.onRootCauseChanged(null);
  }

  clearRootCauseSelection() {
    this.rootCauseComp?.control.reset();
    this.onRootCauseChanged(null);
  }

  isResearchEmrPreferredAction(): boolean {
    const items = this.data?.items || [];
    if(items.length === 0) {
      return false;
    }

    return items.every(i => (i.PreferredAction?.Description || '').trim().toLowerCase() == this.researchEmrPreferredAction);
  }

  onRootCauseChanged(item: FilterSelectItem | null) {
    if(!item || item.Description != 'Other') {
      this.showRcReason = false;
      this.editDialogForm.removeControl(this.rcReasonControlName);
      return;
    }

    this.editDialogForm.addControl(this.rcReasonControlName, this.rcReasonControl);
    this.showRcReason = true;
  }

  onSaveClick(hasExport?: boolean) {
    
    const rcValue = this.rootCauseComp?.control.value;
    const isAllResultsSelection = this.isAllResultsSelection;

    const request = new DiagnosisEventEditRequest();
    request.BatchCount = 1;

    if(this.data.mode == 'single') {
      request.EventIDs = this.singleItem?.ID?.toString();
      request.VisitID = this.singleItem?.VisitID;
      request.DXCode = this.singleItem?.ICDCODE;
    }

    if(this.data.mode == 'batch') {
      request.EventIDs = (!isAllResultsSelection && this.data.items) ? this.data.items.map(i => i.ID).toString() : undefined;
      request.BatchCount = isAllResultsSelection ? this.data.queryResultCount : (this.data.items?.length || 0);
    }

    request.StatusID = 0;
    request.RootCauseID = rcValue ? parseInt(rcValue.ID) : -1;
    request.RootCauseReason = this.rcReasonControl.value || undefined;
    request.Note = this.notesControl.value || undefined;
    request.ActionTaken = this.actionTaken() ? 1 : 0;
    request.Query = isAllResultsSelection ? this.data.query : { IsOpptyQuery: this.data.parent == 'oppty-list' } as DiagnosisEventQuery;
    request.CloseAssociated = this.closeNonTargetedValue ? 1 : 0;
    request.AssociatedCount = this.closeNonTargetedValue ? this.associatedNonTargetedCount : undefined;

    this.loadingSubject.next(true);

    this.opptyService.updateEditListItem(request)
      .pipe(finalize(() => this.loadingSubject.next(false)))
      .subscribe(result => {
        const value = hasExport && this.data.mode == 'batch' ? 'export' : result;
        this.dialogRef.close(value);
      });

  }

  getBatchStats() {
    let statusStatsMap = new Map<string, number>();
    let paStatsMap = new Map<string, number>();

    this.data?.items?.forEach(i => {
      if(i.Status && i.Status.ID) {
        const currentStatsValue = statusStatsMap.get(i.Status.Description);
        const updateValue = currentStatsValue ? currentStatsValue + 1 : 1;
        statusStatsMap.set(i.Status.Description, updateValue);
      }

      if(i.PreferredAction && i.PreferredAction.ID) {
        const currentPaValue = paStatsMap.get(i.PreferredAction.Description);
        const updatePaValue = currentPaValue ? currentPaValue + 1 : 1;
        paStatsMap.set(i.PreferredAction.Description, updatePaValue);
      }
    });

    statusStatsMap = new Map([...statusStatsMap.entries()].sort((a, b) => a[1] - b[1]));
    paStatsMap = new Map([...paStatsMap.entries()].sort((a, b) => a[1] - b[1]));

    statusStatsMap.forEach((v, k) => {
      this.batchStatusStats += ` ${k} (${v}),`;
    });

    paStatsMap.forEach((v, k) => {
      this.batchPreferredActionStats += ` ${k} (${v}),`;
    });

    if(this.batchStatusStats.endsWith(',')) {
      this.batchStatusStats = this.batchStatusStats.substring(0, this.batchStatusStats.length - 1);
    }

    if(this.batchPreferredActionStats.endsWith(',')) {
      this.batchPreferredActionStats = this.batchPreferredActionStats.substring(0, this.batchPreferredActionStats.length - 1);
    }
  }

  payerValue(item: any) {
    if(!item.value) {
      return null;
    }

    return item.value.Abbreviation;
  }

  payerTooltip(item: any) {
    if(!item.value) {
      return;
    }

    return item.value.Name;
  }

  filterOptionValue(item: any) {
    if(!item.value) {
      return null;
    }

    return item.value.Description;
  }

  onGridReady() {
    this.targetedGridReady = true;
    this.targetedGrid?.api?.sizeColumnsToFit();
    this.tryHideInitialLoading();
  }

  onNonTargetedGridReady() {
    this.nonTargetedGridReady = true;
    this.nonTargetedGrid?.api?.sizeColumnsToFit();
    this.tryHideInitialLoading();
  }

  private resetAssociatedNonTargetedState() {
    this.associatedNonTargetedRows = [];
    this.associatedNonTargetedCount = 0;
  }

  private tryHideInitialLoading() {
    const initialGridDataReady = this.targetedGridReady
      && this.nonTargetedDataLoaded
      && this.nonTargetedGridReady
      && this.rootCauseOptionsReady;

    if (initialGridDataReady) {
      this.loadingSubject.next(false);
    }
  }
}
